import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import type { Vehicle, VehicleFirmwareType } from '@data/fms/vehicle/types';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type PostVehicleRegisterBody = {
  environment_id?: string;
  vehicle_name: string;
  email: string;
  firmware_type?: VehicleFirmwareType;
  ros_version: number;
  catalog_id?: string;
  catalog_project_id?: string;
  category: string;
  enable_in_vehicle_app_federation: boolean;
};

/**
 * 新規車両登録API
 */
export const useVehicleRegisterAPI = (): {
  state: APIState;
  postVehicleRegister: (
    body: PostVehicleRegisterBody,
  ) => Promise<Vehicle | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postVehicleRegisterAPI = useCallback(
    async (
      projectId: string,
      body: PostVehicleRegisterBody,
    ): Promise<Result<Vehicle, AxiosResponse>> => {
      try {
        const res = await fmsAPI.post(`/${projectId}/vehicles`, body);
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postVehicleRegister = useAtomCallback(
    useCallback(
      async (get, _, body: PostVehicleRegisterBody) => {
        const project = get(projectAtom);
        if (isNullOrUndefined(project)) {
          notifyError({
            message: t('api.fms.post_vehicle_register', { status: 'failed' }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await postVehicleRegisterAPI(project!.id, body);

        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_vehicle_register', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.post_vehicle_register', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, postVehicleRegisterAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    postVehicleRegister,
  };
};
